import { useState, ReactNode } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(200),
  email: z.string().trim().email("Please enter a valid email").max(320),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
});

interface WaitlistDialogProps {
  trigger: ReactNode;
}

const inputClasses =
  "w-full bg-secondary/50 border border-gold/20 text-foreground placeholder:text-muted-foreground font-body text-sm px-5 py-3.5 focus:outline-none focus:border-gold/60 transition-colors duration-300";

const WaitlistDialog = ({ trigger }: WaitlistDialogProps) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }

    setSubmitting(true);
    const payload = {
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      source: "landing_page",
    };

    const { error } = await supabase.from("leads").insert(payload);

    if (error) {
      if (error.code === "23505") {
        toast.info("You're already registered.", {
          description: "We'll send you everything you need to get started.",
        });
        setForm({ name: "", email: "", phone: "" });
        setOpen(false);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      setSubmitting(false);
      return;
    }


    toast.success("Welcome to the Wealth Transfer Academy!", {
      description: "We'll send you everything you need to get started.",
      duration: 6000,
    });
    setForm({ name: "", email: "", phone: "" });
    setSubmitting(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-background border-gold/30 max-w-md">
        <DialogHeader>
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-body font-medium">
            Join the Academy
          </p>
          <DialogTitle className="text-2xl md:text-3xl font-display font-bold text-foreground leading-tight">
            Reserve Your <span className="text-gradient-gold">Seat</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground font-body text-sm">
            Enrollment is now open for the Wealth Transfer Academy.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <input
            type="text"
            name="name"
            required
            maxLength={200}
            placeholder="Your name"
            value={form.name}
            onChange={handleChange}
            className={inputClasses}
          />
          <input
            type="email"
            name="email"
            required
            maxLength={320}
            placeholder="Email address"
            value={form.email}
            onChange={handleChange}
            className={inputClasses}
          />
          <input
            type="tel"
            name="phone"
            maxLength={30}
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={handleChange}
            className={inputClasses}
          />
          <motion.button
            type="submit"
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full px-8 py-3.5 bg-gradient-gold text-primary-foreground font-body text-sm font-semibold uppercase tracking-[0.2em] transition-all duration-300 hover:shadow-gold disabled:opacity-50"
          >
            {submitting ? "Joining..." : "Join the Academy"}
          </motion.button>
          <p className="text-xs text-muted-foreground/70 font-body text-center">
            No spam. You'll only hear from us about enrollment and academy updates.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
